/**
 * Хеширование для отпечатков.
 *
 * MurmurHash3 x64 128 — не криптографический, но детерминированный и быстрый.
 * Криптостойкость здесь не нужна: хеш служит идентификатором формы данных,
 * а не защитой. Важно другое — результат обязан совпадать байт-в-байт между
 * main thread, воркерами и iframe, иначе cross-context diff даст ложные
 * срабатывания. Поэтому вход всегда нормализуется в UTF-8.
 */

type U64 = [number, number];

function add(m: U64, n: U64): U64 {
  const a = [m[0] >>> 16, m[0] & 0xffff, m[1] >>> 16, m[1] & 0xffff];
  const b = [n[0] >>> 16, n[0] & 0xffff, n[1] >>> 16, n[1] & 0xffff];
  const o = [0, 0, 0, 0];
  o[3] += a[3] + b[3];
  o[2] += o[3] >>> 16;
  o[3] &= 0xffff;
  o[2] += a[2] + b[2];
  o[1] += o[2] >>> 16;
  o[2] &= 0xffff;
  o[1] += a[1] + b[1];
  o[0] += o[1] >>> 16;
  o[1] &= 0xffff;
  o[0] += a[0] + b[0];
  o[0] &= 0xffff;
  return [(o[0] << 16) | o[1], (o[2] << 16) | o[3]];
}

function multiply(m: U64, n: U64): U64 {
  const a = [m[0] >>> 16, m[0] & 0xffff, m[1] >>> 16, m[1] & 0xffff];
  const b = [n[0] >>> 16, n[0] & 0xffff, n[1] >>> 16, n[1] & 0xffff];
  const o = [0, 0, 0, 0];
  o[3] += a[3] * b[3];
  o[2] += o[3] >>> 16;
  o[3] &= 0xffff;
  o[2] += a[2] * b[3];
  o[1] += o[2] >>> 16;
  o[2] &= 0xffff;
  o[2] += a[3] * b[2];
  o[1] += o[2] >>> 16;
  o[2] &= 0xffff;
  o[1] += a[1] * b[3];
  o[0] += o[1] >>> 16;
  o[1] &= 0xffff;
  o[1] += a[2] * b[2];
  o[0] += o[1] >>> 16;
  o[1] &= 0xffff;
  o[1] += a[3] * b[1];
  o[0] += o[1] >>> 16;
  o[1] &= 0xffff;
  o[0] += a[0] * b[3] + a[1] * b[2] + a[2] * b[1] + a[3] * b[0];
  o[0] &= 0xffff;
  return [(o[0] << 16) | o[1], (o[2] << 16) | o[3]];
}

function rotl(m: U64, c: number): U64 {
  const n = c % 64;
  if (n === 0) return m;
  if (n === 32) return [m[1], m[0]];
  if (n < 32) {
    return [(m[0] << n) | (m[1] >>> (32 - n)), (m[1] << n) | (m[0] >>> (32 - n))];
  }
  const k = n - 32;
  return [(m[1] << k) | (m[0] >>> (32 - k)), (m[0] << k) | (m[1] >>> (32 - k))];
}

function shiftLeft(m: U64, c: number): U64 {
  const n = c % 64;
  if (n === 0) return m;
  if (n < 32) return [(m[0] << n) | (m[1] >>> (32 - n)), m[1] << n];
  return [m[1] << (n - 32), 0];
}

function xor(m: U64, n: U64): U64 {
  return [m[0] ^ n[0], m[1] ^ n[1]];
}

function fmix(h: U64): U64 {
  let r = xor(h, [0, h[0] >>> 1]);
  r = multiply(r, [0xff51afd7, 0xed558ccd]);
  r = xor(r, [0, r[0] >>> 1]);
  r = multiply(r, [0xc4ceb9fe, 0x1a85ec53]);
  r = xor(r, [0, r[0] >>> 1]);
  return r;
}

function hex32(n: number): string {
  return (n >>> 0).toString(16).padStart(8, '0');
}

const C1: U64 = [0x87c37b91, 0x114253d5];
const C2: U64 = [0x4cf5ad43, 0x2745937f];

function hashBytes(data: Uint8Array, seed = 0): string {
  const remainder = data.length % 16;
  const blockEnd = data.length - remainder;

  let h1: U64 = [0, seed];
  let h2: U64 = [0, seed];
  let k1: U64;
  let k2: U64;
  let i = 0;

  for (; i < blockEnd; i += 16) {
    k1 = [
      data[i + 4] | (data[i + 5] << 8) | (data[i + 6] << 16) | (data[i + 7] << 24),
      data[i] | (data[i + 1] << 8) | (data[i + 2] << 16) | (data[i + 3] << 24),
    ];
    k2 = [
      data[i + 12] | (data[i + 13] << 8) | (data[i + 14] << 16) | (data[i + 15] << 24),
      data[i + 8] | (data[i + 9] << 8) | (data[i + 10] << 16) | (data[i + 11] << 24),
    ];

    k1 = multiply(rotl(multiply(k1, C1), 31), C2);
    h1 = xor(h1, k1);
    h1 = rotl(h1, 27);
    h1 = add(h1, h2);
    h1 = add(multiply(h1, [0, 5]), [0, 0x52dce729]);

    k2 = multiply(rotl(multiply(k2, C2), 33), C1);
    h2 = xor(h2, k2);
    h2 = rotl(h2, 31);
    h2 = add(h2, h1);
    h2 = add(multiply(h2, [0, 5]), [0, 0x38495ab5]);
  }

  // Хвост короче блока. Развёрнутый вместо switch с проваливанием:
  // байт j (1-based) уходит в k1 при j <= 8 и в k2 при j >= 9.
  k1 = [0, 0];
  k2 = [0, 0];
  for (let j = remainder; j > 0; j--) {
    const byte: U64 = [0, data[i + j - 1]];
    if (j >= 9) {
      k2 = xor(k2, shiftLeft(byte, (j - 9) * 8));
    } else {
      k1 = xor(k1, shiftLeft(byte, (j - 1) * 8));
    }
  }
  k2 = multiply(rotl(multiply(k2, C2), 33), C1);
  h2 = xor(h2, k2);
  k1 = multiply(rotl(multiply(k1, C1), 31), C2);
  h1 = xor(h1, k1);

  h1 = xor(h1, [0, data.length]);
  h2 = xor(h2, [0, data.length]);
  h1 = add(h1, h2);
  h2 = add(h2, h1);
  h1 = fmix(h1);
  h2 = fmix(h2);
  h1 = add(h1, h2);
  h2 = add(h2, h1);

  return hex32(h1[0]) + hex32(h1[1]) + hex32(h2[0]) + hex32(h2[1]);
}

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

function toBytes(input: string): Uint8Array {
  if (encoder) return encoder.encode(input);
  // Крайне маловероятный фолбэк: ручная кодировка UTF-8.
  const out: number[] = [];
  for (let i = 0; i < input.length; i++) {
    let c = input.charCodeAt(i);
    if (c < 0x80) {
      out.push(c);
    } else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c >= 0xd800 && c <= 0xdbff && i + 1 < input.length) {
      const next = input.charCodeAt(++i);
      c = 0x10000 + ((c & 0x3ff) << 10) + (next & 0x3ff);
      out.push(
        0xf0 | (c >> 18),
        0x80 | ((c >> 12) & 0x3f),
        0x80 | ((c >> 6) & 0x3f),
        0x80 | (c & 0x3f),
      );
    } else {
      out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return new Uint8Array(out);
}

/** Хеш строки или байтового буфера. Всегда 32 hex-символа. */
export function hash(input: string | Uint8Array, seed = 0): string {
  return hashBytes(typeof input === 'string' ? toBytes(input) : input, seed);
}

/**
 * Детерминированная сериализация: ключи объектов сортируются, специальные
 * значения кодируются явно. Порядок ключей в JS зависит от порядка вставки,
 * а он может отличаться между контекстами — для diff это недопустимо.
 */
export function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();

  const walk = (v: unknown): string => {
    if (v === null) return 'null';
    if (v === undefined) return '?';

    switch (typeof v) {
      case 'number':
        if (Number.isNaN(v)) return 'NaN';
        if (v === Infinity) return 'Inf';
        if (v === -Infinity) return '-Inf';
        // -0 и 0 должны различаться: разное поведение движков это выдаёт.
        return Object.is(v, -0) ? '-0' : String(v);
      case 'string':
        return JSON.stringify(v);
      case 'boolean':
        return v ? 'true' : 'false';
      case 'bigint':
        return `${v}n`;
      case 'function':
        return `fn:${(v as { name?: string }).name ?? ''}`;
      case 'symbol':
        return `sym:${String(v)}`;
      case 'object':
        break;
      default:
        return `?${typeof v}`;
    }

    const obj = v as object;
    if (seen.has(obj)) return '[circular]';
    seen.add(obj);

    let out: string;
    if (Array.isArray(obj)) {
      out = `[${obj.map(walk).join(',')}]`;
    } else if (ArrayBuffer.isView(obj)) {
      out = `<${Array.from(obj as unknown as ArrayLike<number>).join(',')}>`;
    } else {
      const keys = Object.keys(obj as Record<string, unknown>).sort();
      out = `{${keys
        .map((k) => `${JSON.stringify(k)}:${walk((obj as Record<string, unknown>)[k])}`)
        .join(',')}}`;
    }

    seen.delete(obj);
    return out;
  };

  return walk(value);
}

/** Хеш произвольной структуры через детерминированную сериализацию. */
export function hashValue(value: unknown, seed = 0): string {
  return hash(stableStringify(value), seed);
}

/** Короткий хеш для случаев, где хватает 8 символов (метки в UI). */
export function shortHash(input: string | Uint8Array): string {
  return hash(input).slice(0, 8);
}
